import { useCallback, useState } from "react";

const toggleInSet = (set: Set<number>, id: number) => {
  const next = new Set(set);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

export const useTableSelection = () => {
  const [isAllMode, setIsAllMode] = useState(false);
  const [includedIds, setIncludedIds] = useState<Set<number>>(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());

  const isSelected = useCallback((id: number) => (isAllMode ? !excludedIds.has(id) : includedIds.has(id)), [isAllMode, includedIds, excludedIds]);

  const toggle = useCallback(
    (id: number) => {
      if (isAllMode) {
        setExcludedIds((prev) => toggleInSet(prev, id));
      } else {
        setIncludedIds((prev) => toggleInSet(prev, id));
      }
    },
    [isAllMode],
  );

  const toggleAll = useCallback((ids: number[]) => {
    setIncludedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  }, []);

  const isAllSelected = useCallback((ids: number[]) => ids.length > 0 && ids.every((id) => isSelected(id)), [isSelected]);

  const toggleSelectAll = useCallback(() => {
    setIsAllMode((prev) => !prev);
    setIncludedIds(new Set());
    setExcludedIds(new Set());
  }, []);

  const clear = useCallback(() => {
    setIsAllMode(false);
    setIncludedIds(new Set());
    setExcludedIds(new Set());
  }, []);

  const selectedCount = useCallback(
    (totalCount: number) => (isAllMode ? Math.max(totalCount - excludedIds.size, 0) : includedIds.size),
    [isAllMode, includedIds, excludedIds],
  );

  return {
    isAllMode,
    includedIds,
    excludedIds,
    isSelected,
    toggle,
    toggleAll,
    isAllSelected,
    toggleSelectAll,
    clear,
    selectedCount,
  };
};
